/**
 * LoginScreen — /login
 *
 * Centred single-column form. Dutch labels per PROJECT_BRIEF.md §10.
 * Two TOTP paths, both handled inline on this screen:
 *  - already-enrolled users get `/problems/totp-required`; we reveal the code
 *    field and resubmit.
 *  - admin/operator on their FIRST login get `/problems/totp-enrollment-
 *    required`; we fetch the QR via `enrollTotp(email, password)`, show it, and
 *    once they enter a code the resubmit confirms enrolment + logs them in.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import { LamaIcon } from '@/components/LamaIcon';
import { ApiError } from '@/lib/api';
import { enrollTotp, login, type TotpSetupResponse } from '@/lib/auth';

type Props = {
  /** Called on successful login; defaults to `window.location` redirect. */
  onSuccess?: () => void;
  /** Optional path to redirect to after login. Read from `?next=` by default. */
  nextPath?: string;
};

/** Backend problem-detail types that indicate TOTP is required/invalid. */
const TOTP_PROBLEM_TYPES = new Set([
  'about:blank/totp-required',
  '/problems/totp-required',
  '/problems/totp-invalid',
]);

function getNextFromQuery(): string {
  if (typeof window === 'undefined') return '/';
  const params = new URLSearchParams(window.location.search);
  const raw = params.get('next');
  if (!raw) return '/';
  // Only allow same-origin relative redirects.
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  return '/';
}

export function LoginScreen({ onSuccess, nextPath }: Props): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [totpRequired, setTotpRequired] = useState(false);
  const [enroll, setEnroll] = useState<TotpSetupResponse | null>(null);
  const [qrSvg, setQrSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const totpInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-focus the TOTP input as soon as it appears.
  useEffect(() => {
    if (totpRequired) totpInputRef.current?.focus();
  }, [totpRequired]);

  // Render the enrollment QR: prefer the backend SVG, else render it client-side
  // from the otpauth URL (the backend's `segno` is optional and usually absent,
  // so this lazy `qrcode` fallback is what actually shows the scannable code).
  useEffect(() => {
    if (!enroll) {
      setQrSvg(null);
      return;
    }
    if (enroll.qr_svg) {
      setQrSvg(enroll.qr_svg);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const mod = (await import('qrcode')) as {
          toString: (
            text: string,
            opts: { type: 'svg'; margin: number; width: number },
          ) => Promise<string>;
        };
        const svg = await mod.toString(enroll.otpauth_url, { type: 'svg', margin: 1, width: 160 });
        if (!cancelled) setQrSvg(svg);
      } catch {
        // Leave qrSvg null; the manual secret below is still shown.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enroll]);

  const target = useMemo(() => nextPath ?? getNextFromQuery(), [nextPath]);

  const handleSubmit = useCallback(
    async (evt: FormEvent<HTMLFormElement>) => {
      evt.preventDefault();
      setError(null);
      setSubmitting(true);
      try {
        await login({
          email: email.trim(),
          password,
          totp_code: totpRequired && totpCode ? totpCode.trim() : undefined,
        });
        if (onSuccess) {
          onSuccess();
        } else if (typeof window !== 'undefined') {
          window.location.assign(target);
        }
      } catch (err) {
        if (err instanceof ApiError) {
          const problemType = err.problem.type ?? '';

          if (problemType.endsWith('totp-enrollment-required')) {
            // First login for an admin/operator: fetch the provisioning QR
            // (the backend re-validates the password we just sent), then reveal
            // it + the code field. Entering a code resubmits and confirms.
            try {
              const data = await enrollTotp({ email: email.trim(), password });
              setEnroll(data);
              setTotpRequired(true);
              setError(
                'Tweestapsverificatie instellen: scan de QR-code met je authenticator-app ' +
                  'en voer de 6-cijferige code in om te activeren.',
              );
            } catch {
              setError(
                'Kon het instellen van tweestapsverificatie niet starten. ' +
                  'Controleer je gegevens en probeer opnieuw.',
              );
            }
          } else if (
            TOTP_PROBLEM_TYPES.has(problemType) ||
            problemType.endsWith('totp-required') ||
            problemType.endsWith('totp-invalid')
          ) {
            setTotpRequired(true);
            setError(err.problem.detail ?? 'TOTP-code vereist. Voer de 6-cijferige code in.');
          } else {
            setError(
              err.problem.detail ??
                err.problem.title ??
                'Aanmelden is mislukt. Controleer je gegevens en probeer opnieuw.',
            );
          }
        } else {
          setError('Onbekende fout bij aanmelden. Probeer het opnieuw.');
        }
      } finally {
        setSubmitting(false);
      }
    },
    [email, password, totpCode, totpRequired, onSuccess, target],
  );

  return (
    <div className="flex min-h-screen items-stretch bg-[var(--color-surface-base,#f8fafc)]">
      <header className="absolute left-0 top-0 flex items-center gap-2 p-6">
        <span
          aria-hidden
          className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[var(--color-brand-600,#70b000)] text-white"
        >
          <LamaIcon size={20} />
        </span>
        <span className="font-semibold text-[var(--color-text-fg,#0f172a)]">RuaHQ</span>
      </header>

      <main className="flex flex-1 items-center justify-center px-4">
        <form
          onSubmit={handleSubmit}
          noValidate
          className="w-full max-w-sm rounded-xl border border-[var(--color-border-default,#e2e8f0)] bg-white p-8 shadow-sm"
          aria-labelledby="login-title"
        >
          <h1
            id="login-title"
            className="mb-1 text-xl font-semibold text-[var(--color-text-fg,#0f172a)]"
          >
            Aanmelden bij RuaHQ
          </h1>
          <p className="mb-6 text-sm text-[var(--color-text-fg-muted,#64748b)]">
            Gebruik je SerSo-account om verder te gaan.
          </p>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="login-email"
                className="mb-1 block text-sm font-medium text-[var(--color-text-fg,#0f172a)]"
              >
                E-mailadres
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-[var(--color-border-default,#cbd5e1)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-brand-600,#70b000)] focus:ring-2 focus:ring-[var(--color-brand-200,#c8e388)]"
              />
            </div>

            <div>
              <label
                htmlFor="login-password"
                className="mb-1 block text-sm font-medium text-[var(--color-text-fg,#0f172a)]"
              >
                Wachtwoord
              </label>
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                className="w-full rounded-md border border-[var(--color-border-default,#cbd5e1)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-brand-600,#70b000)] focus:ring-2 focus:ring-[var(--color-brand-200,#c8e388)]"
              />
            </div>

            {enroll && (
              <div className="rounded-md border border-[var(--color-border-default,#e2e8f0)] bg-[var(--color-surface-base,#f8fafc)] p-4 text-center">
                <p className="mb-3 text-sm font-medium text-[var(--color-text-fg,#0f172a)]">
                  Scan met je authenticator-app
                </p>
                {/* QR SVG derived from the otpauth URL (server- or client-rendered); no user input. */}
                {qrSvg ? (
                  <div
                    aria-label="QR-code voor tweestapsverificatie"
                    className="mx-auto inline-block [&>svg]:h-40 [&>svg]:w-40"
                    dangerouslySetInnerHTML={{ __html: qrSvg }}
                  />
                ) : null}
                <p className="mt-3 break-all font-mono text-xs text-[var(--color-text-fg-muted,#64748b)]">
                  of voer de sleutel handmatig in: {enroll.secret}
                </p>
              </div>
            )}

            {totpRequired && (
              <div>
                <label
                  htmlFor="login-totp"
                  className="mb-1 block text-sm font-medium text-[var(--color-text-fg,#0f172a)]"
                >
                  TOTP-code
                </label>
                <input
                  id="login-totp"
                  ref={totpInputRef}
                  name="totp_code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  disabled={submitting}
                  aria-describedby="login-totp-hint"
                  className="w-full rounded-md border border-[var(--color-border-default,#cbd5e1)] bg-white px-3 py-2 font-mono text-sm tracking-widest outline-none focus:border-[var(--color-brand-600,#70b000)] focus:ring-2 focus:ring-[var(--color-brand-200,#c8e388)]"
                />
                <p
                  id="login-totp-hint"
                  className="mt-1 text-xs text-[var(--color-text-fg-muted,#64748b)]"
                >
                  Voer de 6-cijferige code uit je authenticator-app in.
                </p>
              </div>
            )}

            {error && (
              <div
                role="alert"
                aria-live="polite"
                className="rounded-md border border-[var(--color-fail-600,#dc2626)] bg-[var(--color-fail-50,#fef2f2)] px-3 py-2 text-sm text-[var(--color-fail-700,#b91c1c)]"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-[var(--color-brand-600,#70b000)] px-3 py-2 text-sm font-semibold text-[var(--color-fg-on-brand,#0f172a)] hover:bg-[var(--color-brand-700,#5a8c00)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-200,#c8e388)] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? 'Bezig met aanmelden…' : 'Aanmelden'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}

export default LoginScreen;
